import Markdown, { type Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { cn } from '@/components/ui/cn';

// A light highlight.js theme for fenced code blocks. Imported once here; Vite
// inlines it at build time.
import 'highlight.js/styles/github.css';

/**
 * Renders assistant Markdown safely (spec 0016).
 *
 * Safety posture (ADR-0002):
 * - react-markdown builds a React vdom and never uses dangerouslySetInnerHTML.
 * - Raw HTML in the content is NOT parsed into live DOM: rehype-raw is
 *   deliberately not enabled, so `<script>` / `onerror` never activate.
 * - Link and image URLs are scheme-sanitized by react-markdown's default
 *   urlTransform, which neutralizes javascript:/vbscript:/data: URLs.
 *
 * Rendering is live during streaming: this component simply re-renders as the
 * accumulated `content` grows, so partial Markdown settles into final Markdown
 * as more tokens arrive.
 */

const components: Components = {
  h1: ({ node: _node, ...props }) => (
    <h1 className="mb-1 mt-3 text-lg font-semibold first:mt-0" {...props} />
  ),
  h2: ({ node: _node, ...props }) => (
    <h2 className="mb-1 mt-3 text-base font-semibold first:mt-0" {...props} />
  ),
  h3: ({ node: _node, ...props }) => (
    <h3 className="mb-1 mt-2 text-sm font-semibold first:mt-0" {...props} />
  ),
  h4: ({ node: _node, ...props }) => (
    <h4 className="mb-1 mt-2 text-sm font-semibold first:mt-0" {...props} />
  ),
  h5: ({ node: _node, ...props }) => (
    <h5 className="mb-1 mt-2 text-sm font-semibold first:mt-0" {...props} />
  ),
  h6: ({ node: _node, ...props }) => (
    <h6 className="mb-1 mt-2 text-sm font-semibold first:mt-0" {...props} />
  ),
  p: ({ node: _node, ...props }) => (
    <p className="my-2 leading-relaxed first:mt-0 last:mb-0" {...props} />
  ),
  ul: ({ node: _node, ...props }) => (
    <ul className="my-2 list-disc space-y-0.5 pl-5 first:mt-0 last:mb-0" {...props} />
  ),
  ol: ({ node: _node, ...props }) => (
    <ol className="my-2 list-decimal space-y-0.5 pl-5 first:mt-0 last:mb-0" {...props} />
  ),
  li: ({ node: _node, ...props }) => <li className="leading-relaxed" {...props} />,
  blockquote: ({ node: _node, ...props }) => (
    <blockquote
      className="my-2 border-l-2 border-gray-300 pl-3 italic text-gray-600"
      {...props}
    />
  ),
  a: ({ node: _node, ...props }) => (
    <a
      className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
      {...props}
      target="_blank"
      rel="noopener noreferrer"
    />
  ),
  img: ({ node: _node, ...props }) => (
    // Constrained to the bubble width so a wide/broken image never breaks layout.
    // Dangerous-scheme srcs are already neutralized by react-markdown's
    // urlTransform before reaching here.
    <img {...props} alt={typeof props.alt === 'string' ? props.alt : ''} className="my-2 max-w-full rounded" />
  ),
  table: ({ node: _node, ...props }) => (
    <div data-testid="md-table-scroll" className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-left text-xs" {...props} />
    </div>
  ),
  th: ({ node: _node, ...props }) => (
    <th className="border border-gray-300 px-2 py-1 font-semibold" {...props} />
  ),
  td: ({ node: _node, ...props }) => <td className="border border-gray-300 px-2 py-1" {...props} />,
  pre: ({ node: _node, ...props }) => (
    <pre
      className="my-2 overflow-x-auto rounded border border-gray-200 bg-gray-50 p-3 text-xs"
      {...props}
    />
  ),
  code: ({ node: _node, className, children, ...props }) => {
    // Fenced blocks carry a language- class (tagged) or, when untagged, still
    // arrive with their trailing newline; inline code has neither. Only inline
    // code gets the pill styling -- block code is styled by <pre> + hljs.
    const isBlock = /\blanguage-/.test(className ?? '') || String(children ?? '').includes('\n');
    if (isBlock) {
      return (
        <code className={cn('font-mono', className)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-gray-200 px-1 py-0.5 font-mono text-[0.85em]" {...props}>
        {children}
      </code>
    );
  },
  hr: ({ node: _node, ...props }) => <hr className="my-3 border-gray-200" {...props} />,
};

export function MarkdownMessage({ content }: { content: string }) {
  return (
    <div className="markdown break-words text-sm leading-relaxed">
      <Markdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[[rehypeHighlight, { detect: false, ignoreMissing: true }]]}
        components={components}
      >
        {content}
      </Markdown>
    </div>
  );
}
